/**
 * バトル履歴エントリ
 *
 * 1回のバトル結果を記録する。
 */
export interface BattleHistoryEntry {
  /** ユニークID */
  id: string;
  /** 記録日時（ISO 8601形式） */
  date: string;
  /** バトル結果 */
  resultType: 'PLAYER1_WIN' | 'PLAYER2_WIN' | 'DRAW';
  /** ゲームモード */
  gameMode: 'CHALLENGE' | 'FREE_CPU' | 'FREE_CPU_3V3' | 'FREE_LOCAL' | 'ONLINE';
  /** プレイヤーのモンスターID */
  playerMonsterId: string;
  /** 相手のモンスターID */
  opponentMonsterId: string;
  /** 総ターン数 */
  turnCount: number;
  /** プレイヤーの最終HP */
  playerFinalHp: number;
  /** 相手の最終HP */
  opponentFinalHp: number;
}

/**
 * バトル履歴全体
 */
export interface BattleHistory {
  /** バトル履歴（最新順） */
  entries: BattleHistoryEntry[];
}

/** デフォルトのバトル履歴（空状態） */
export const DEFAULT_BATTLE_HISTORY: BattleHistory = {
  entries: [],
};

/** 有効な resultType の値 */
const VALID_RESULT_TYPES: BattleHistoryEntry['resultType'][] = [
  'PLAYER1_WIN',
  'PLAYER2_WIN',
  'DRAW',
];

/** 有効な gameMode の値 */
const VALID_GAME_MODES: BattleHistoryEntry['gameMode'][] = [
  'CHALLENGE',
  'FREE_CPU',
  'FREE_CPU_3V3',
  'FREE_LOCAL',
  'ONLINE',
];

/**
 * BattleHistoryEntry 型のバリデーション
 */
function isValidBattleHistoryEntry(obj: unknown): obj is BattleHistoryEntry {
  if (obj === null || obj === undefined || typeof obj !== 'object') return false;
  const e = obj as Record<string, unknown>;

  return (
    typeof e.id === 'string' &&
    e.id.length > 0 &&
    typeof e.date === 'string' &&
    e.date.length > 0 &&
    typeof e.resultType === 'string' &&
    (VALID_RESULT_TYPES as string[]).includes(e.resultType) &&
    typeof e.gameMode === 'string' &&
    (VALID_GAME_MODES as string[]).includes(e.gameMode) &&
    typeof e.playerMonsterId === 'string' &&
    typeof e.opponentMonsterId === 'string' &&
    typeof e.turnCount === 'number' &&
    typeof e.playerFinalHp === 'number' &&
    typeof e.opponentFinalHp === 'number'
  );
}

/**
 * BattleHistory 型のバリデーション
 */
export function isValidBattleHistory(obj: unknown): obj is BattleHistory {
  if (obj === null || obj === undefined || typeof obj !== 'object') return false;
  const candidate = obj as Record<string, unknown>;

  if (!Array.isArray(candidate.entries)) return false;

  return candidate.entries.every((entry) => isValidBattleHistoryEntry(entry));
}
